import { useEffect, useRef, useState } from 'react'
import { useHealthRepository } from '@/app/AppProviders'
import { MOODS, shiftDay, type MoodRecord, type MoodValue } from '@/domain/health/journal'
import { localDayAt, nextLocalDayStart } from '@/domain/health/time'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { MoodDumpling } from './MoodDumpling'

export function MoodCard({ timeZone, referenceAt }: { timeZone: string; referenceAt?: string }) {
  const repository = useHealthRepository()
  const [today, setToday] = useState(() => localDayAt(referenceAt ?? new Date().toISOString(), timeZone))
  const [day, setDay] = useState(today)
  const [records, setRecords] = useState<MoodRecord[] | null>(null)
  const [error, setError] = useState(false)
  const [version, setVersion] = useState(0)
  const [busy, setBusy] = useState(false)
  const [history, setHistory] = useState(false)
  useEffect(() => {
    let active = true
    void (async () => {
      try {
        const data = await repository.getMoodHistory()
        if (active) { setRecords(data); setError(false) }
      } catch { if (active) setError(true) }
    })()
    return () => { active = false }
  }, [repository, version])
  useEffect(() => {
    if (referenceAt) return
    const refresh = () => {
      const next = localDayAt(new Date().toISOString(), timeZone)
      if (next !== today && !busy) { setToday(next); setDay((value) => value === today ? next : value) }
    }
    const current = new Date().toISOString()
    const delay = localDayAt(current, timeZone) !== today
      ? 0 : Math.max(1000, Date.parse(nextLocalDayStart(current, timeZone)) - Date.now() + 1)
    const timer = window.setTimeout(refresh, delay)
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => { clearTimeout(timer); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh) }
  }, [today, timeZone, referenceAt, busy])
  const record = records?.find((value) => value.localDay === day)
  return <section className="mood-card" aria-labelledby="mood-heading">
    <div className="journal-heading"><div><p className="journal-eyebrow">一点心情，留给自己</p><h2 id="mood-heading">{day === today ? '今日心情' : '那天的心情'}</h2></div>
      <button type="button" className="journal-text-button" aria-expanded={history} disabled={busy} onClick={() => setHistory(!history)}>记录</button>
    </div>
    {history ? <div className="mood-history">
      <label>心情日期<input type="date" value={day} max={today} disabled={busy} onChange={(event) => { if (event.target.value) setDay(event.target.value) }} /></label>
      <button type="button" disabled={busy} onClick={() => setDay(today)}>今天</button>
      {records?.length ? <select aria-label="以往心情" value={record ? day : ''} disabled={busy} onChange={(event) => { if (event.target.value) setDay(event.target.value) }}>
        <option value="">以往心情</option>{records.map((item) => <option key={item.localDay} value={item.localDay}>{item.localDay} · {MOODS[item.mood - 1]!.label}</option>)}
      </select> : null}
    </div> : null}
    {error ? <p role="alert">心情读取失败 <button type="button" disabled={busy} onClick={() => setVersion(version + 1)}>重试</button></p>
      : records === null ? <p role="status">正在读取心情…</p>
      : <MoodEditor key={day} day={day} record={record} timeZone={timeZone} referenceAt={referenceAt}
          onBusy={setBusy} onSaved={setRecords} />}
    <div className="mood-week" aria-label="最近7天心情">
      {Array.from({ length: 7 }, (_, index) => shiftDay(today, index - 6)).map((date) => {
        const mood = records?.find((item) => item.localDay === date)?.mood
        return <button key={date} type="button" disabled={busy || error || records === null} aria-pressed={day === date}
          aria-label={`${date}，${mood ? MOODS[mood - 1]!.label : '未记录'}`} onClick={() => setDay(date)}>
          {mood ? <MoodDumpling mood={mood} /> : <span className="mood-week__empty" aria-hidden="true" />}
          <small>{date === today ? '今天' : `${Number(date.slice(5, 7))}/${Number(date.slice(8))}`}</small>
        </button>
      })}
    </div>
  </section>
}

function MoodEditor({ day, record, timeZone, referenceAt, onSaved, onBusy }: {
  day: string; record?: MoodRecord; timeZone: string; referenceAt?: string
  onSaved: (records: MoodRecord[]) => void; onBusy: (busy: boolean) => void
}) {
  const repository = useHealthRepository()
  const [note, setNote] = useState(record?.note ?? '')
  const [noteOpen, setNoteOpen] = useState(Boolean(record?.note))
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [deleting, setDeleting] = useState(false)
  const lock = useRef(false)
  const save = async (mood?: MoodValue) => {
    if (lock.current) return
    lock.current = true; setBusy(true); onBusy(true); setError(''); setMessage('')
    try {
      if (mood) await repository.saveMood(day, timeZone, mood, note, referenceAt ? new Date(referenceAt) : new Date())
      else await repository.deleteMood(day)
      onSaved(await repository.getMoodHistory())
      if (!mood) { setNote(''); setNoteOpen(false) }
      setDeleting(false)
      setMessage(mood ? '已记录' : '已删除')
    } catch (cause) { setError(cause instanceof Error ? cause.message : '保存失败，请重试') }
    finally { lock.current = false; setBusy(false); onBusy(false) }
  }
  return <div className="mood-editor" aria-busy={busy}>
    <p className="mood-editor__prompt">{record ? `${day.slice(5).replace('-', '/')} · ${MOODS[record.mood - 1]!.label}` : '选一颗，记下心情'}</p>
    <div className="mood-choices" role="group" aria-label="选择心情">
      {MOODS.map(({ value, label }) => <button key={value} type="button" aria-pressed={record?.mood === value} disabled={busy}
        onClick={() => void save(value)}><MoodDumpling mood={value} /><span>{label}</span></button>)}
    </div>
    <div className="journal-heading mood-editor__actions"><button type="button" className="journal-text-button" disabled={busy} aria-expanded={noteOpen} onClick={() => setNoteOpen(!noteOpen)}>写一句</button>
      {record ? <button type="button" className="journal-text-button" disabled={busy} onClick={() => setDeleting(true)}>删除心情</button> : null}</div>
    {noteOpen ? <form onSubmit={(event) => { event.preventDefault(); if (record) void save(record.mood) }} className="mood-note">
      <label>一句心情<textarea placeholder="想写什么都可以…" value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} rows={2} /></label>
      <div className="journal-heading"><small>{[...note].length}/200</small><button type="submit" disabled={busy || !record || [...note.trim()].length > 200}>保存备注</button></div>
      {!record ? <small>选一颗团子，一起保存</small> : null}
    </form> : null}
    {message ? <p className="journal-feedback" role="status">{message}</p> : null}
    {error ? <p role="alert" className="form-error">{error}</p> : null}
    <ConfirmDialog open={deleting} title="删除这天的心情？" description={day} confirmLabel={busy ? '删除中…' : '删除'} onConfirm={() => void save()} onCancel={() => { if (!busy) setDeleting(false) }} />
  </div>
}
